/**
 * React Query hook for Sankey chart data.
 *
 * The backend applies a 12-hour server-side cache on Sankey computations.
 * We match that with a 12-hour client-side stale time.
 */

import { useQuery } from '@tanstack/react-query';
import { generateSankeyChart } from '../../../lib/api/financialApi';
import type { SankeyRequest } from '../../../types/financial';

const STALE_TIME_12_HOURS = 1000 * 60 * 60 * 12;

/**
 * Fetch Sankey chart data for a company's revenue-to-EV flow.
 *
 * @param request - { company_name, year, chart_type }
 */
export const useSankeyData = (request: SankeyRequest | null) =>
  useQuery({
    queryKey: [
      'financial',
      'sankey',
      request?.company_name,
      request?.year,
      request?.chart_type ?? 'revenue_to_ev',
    ],
    queryFn: () => generateSankeyChart(request!),
    enabled: !!request?.company_name,
    staleTime: STALE_TIME_12_HOURS,
  });
